import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Gift, History, LockKeyhole, Sparkles } from "lucide-react";
import { PageHeading, PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import {
  EmptyState,
  ErrorState,
  ProductSkeletonList,
} from "@/components/states";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import { useAuth } from "@/lib/auth";
import { formatDate, formatPrice } from "@/lib/format";
import { useI18n } from "@/lib/i18n";
import { loyaltyWalletQuery } from "@/lib/queries";
import { featureEnabled, useStorefront } from "@/lib/storefront";

export const Route = createFileRoute("/loyalty")({
  component: LoyaltyWalletPage,
});

function LoyaltyWalletPage() {
  const { t, lang } = useI18n();
  const storefront = useStorefront();
  const { user } = useAuth();
  const available =
    storefront.mode !== "preview" && featureEnabled(storefront, "loyalty");
  const { ready } = useRequireAuth(available);
  const wallet = useQuery({
    ...loyaltyWalletQuery(user?.id ?? ""),
    enabled: ready && available,
  });

  if (!available) {
    return (
      <PageShell size="narrow">
        <EmptyState
          title={t("loyalty.unavailableTitle")}
          description={t("loyalty.unavailableDescription")}
          icon={<LockKeyhole className="size-7" aria-hidden="true" />}
          action={
            <Button asChild variant="outline">
              <Link to="/menu">{t("loyalty.browseMenu")}</Link>
            </Button>
          }
        />
      </PageShell>
    );
  }

  if (!ready || wallet.isPending) {
    return (
      <PageShell size="narrow">
        <ProductSkeletonList count={2} />
      </PageShell>
    );
  }

  if (wallet.isError || !wallet.data) {
    return (
      <PageShell size="narrow">
        <ErrorState error={wallet.error} onRetry={() => wallet.refetch()} />
      </PageShell>
    );
  }

  const data = wallet.data;
  return (
    <PageShell size="narrow">
      <PageHeading
        breadcrumbs={[
          { label: t("nav.home"), to: "/" },
          { label: t("nav.loyalty") },
        ]}
        title={t("loyalty.walletTitle")}
        description={t("loyalty.walletSubtitle")}
      />

      <section className="pattern-levant surface-card mb-6 overflow-hidden p-5 sm:p-7">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <p className="text-sm font-bold text-muted-foreground">
              {t("loyalty.availablePoints")}
            </p>
            <p className="mt-1 text-4xl font-black text-primary tabular-nums">
              {formatPoints(data.account.availablePoints, lang)}
            </p>
          </div>
          <span className="grid size-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-soft">
            <Gift className="size-7" aria-hidden="true" />
          </span>
        </div>

        {data.account.heldPoints > 0 ? (
          <p className="mt-4 rounded-xl bg-card/80 px-3 py-2 text-sm font-medium">
            {t("loyalty.heldPoints", {
              points: formatPoints(data.account.heldPoints, lang),
            })}
          </p>
        ) : null}

        {data.program ? (
          <div className="mt-6">
            <div className="mb-2 flex flex-wrap items-end justify-between gap-2 text-sm">
              <span className="font-bold">
                {data.program.status === "paused"
                  ? t("loyalty.paused")
                  : data.account.rewardEligible
                  ? t("loyalty.pointsReady")
                  : t("loyalty.pointsToReward", {
                      points: formatPoints(data.account.pointsToReward, lang),
                    })}
              </span>
              <span className="text-muted-foreground">
                {t("loyalty.rewardValue", {
                  points: formatPoints(data.program.rewardPoints, lang),
                  amount: formatPrice(
                    data.program.rewardDiscountMinor,
                    lang,
                    data.program.currency,
                  ),
                })}
              </span>
            </div>
            {data.program.status === "active" ? (
              <p className="mt-3 text-xs text-muted-foreground">
                {t("loyalty.earnRule", {
                    points: data.program.pointsPerCurrencyUnit,
                    currency: data.program.currency,
                  })}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mt-5 text-sm text-muted-foreground">
            {t("loyalty.noProgram")}
          </p>
        )}
      </section>

      <section
        className="surface-card p-5 sm:p-6"
        aria-labelledby="loyalty-activity-title"
      >
        <div className="mb-4 flex items-center gap-2">
          <History className="size-5 text-primary" aria-hidden="true" />
          <h2 id="loyalty-activity-title" className="text-lg font-black">
            {t("loyalty.activityTitle")}
          </h2>
        </div>
        {data.activity.length ? (
          <div className="divide-y divide-border">
            {data.activity.map((entry) => (
              <article
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="font-bold">
                    {t(`loyalty.activity.${entry.kind}`)}
                  </p>
                  <time
                    dateTime={entry.createdAt}
                    className="mt-1 block text-xs text-muted-foreground"
                  >
                    {formatDate(entry.createdAt, lang, true)}
                  </time>
                  {entry.kind === "adjustment" && entry.reason ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {entry.reason}
                    </p>
                  ) : null}
                  {entry.orderId ? (
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: entry.orderId }}
                      className="mt-1 inline-block text-xs font-bold text-primary underline-offset-4 hover:underline"
                    >
                      {t("loyalty.viewOrder")}
                    </Link>
                  ) : null}
                </div>
                <div className="text-end">
                  {entry.availableDeltaPoints !== 0 ? (
                    <p
                      className={
                        entry.availableDeltaPoints > 0
                          ? "font-black text-success"
                          : "font-black text-destructive"
                      }
                    >
                      {signedPoints(entry.availableDeltaPoints, lang)}
                    </p>
                  ) : null}
                  {entry.heldDeltaPoints !== 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {t("loyalty.reservedChange", {
                        points: signedPoints(entry.heldDeltaPoints, lang),
                      })}
                    </p>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Sparkles
              className="mx-auto size-7 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="mt-3 font-bold">{t("loyalty.emptyActivity")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("loyalty.emptyActivityDescription")}
            </p>
          </div>
        )}
      </section>
    </PageShell>
  );
}

function formatPoints(value: number, lang: "ar" | "en") {
  return new Intl.NumberFormat(
    lang === "ar" ? "ar-u-nu-latn" : "en-u-nu-latn",
    {
      numberingSystem: "latn",
    },
  ).format(value);
}

function signedPoints(value: number, lang: "ar" | "en") {
  return new Intl.NumberFormat(
    lang === "ar" ? "ar-u-nu-latn" : "en-u-nu-latn",
    {
      signDisplay: "always",
      numberingSystem: "latn",
    },
  ).format(value);
}
